import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, X, Loader2, Columns2, Layers } from "lucide-react";
import PhotoOverlayView from "./PhotoOverlayView";

interface ProgressPhoto {
  id: string;
  storage_path: string;
  photo_type: string;
  photo_date: string;
  caption: string | null;
}

interface PhotoCompareViewProps {
  photos: ProgressPhoto[];
  onClose?: () => void;
}

const photoTypeLabels: Record<string, string> = {
  front: "Front",
  side: "Side",
  back: "Back",
  other: "Other",
};

type CompareMode = "side-by-side" | "overlay";

const PhotoCompareView = ({ photos, onClose }: PhotoCompareViewProps) => {
  const [mode, setMode] = useState<CompareMode>("side-by-side");
  const [leftIndex, setLeftIndex] = useState(0);
  const [rightIndex, setRightIndex] = useState(Math.min(1, photos.length - 1));
  const [leftUrl, setLeftUrl] = useState<string | null>(null);
  const [rightUrl, setRightUrl] = useState<string | null>(null);
  const [loadingLeft, setLoadingLeft] = useState(true);
  const [loadingRight, setLoadingRight] = useState(true);

  const loadImageUrl = async (
    storagePath: string,
    setUrl: (url: string | null) => void,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.storage
        .from("progress-photos")
        .createSignedUrl(storagePath, 3600);

      if (error) throw error;
      setUrl(data.signedUrl);
    } catch (err) {
      console.error("Error loading image:", err);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (photos[leftIndex]) {
      loadImageUrl(photos[leftIndex].storage_path, setLeftUrl, setLoadingLeft);
    }
  }, [leftIndex, photos]);

  useEffect(() => {
    if (photos[rightIndex]) {
      loadImageUrl(
        photos[rightIndex].storage_path,
        setRightUrl,
        setLoadingRight
      );
    }
  }, [rightIndex, photos]);

  const leftPhoto = photos[leftIndex];
  const rightPhoto = photos[rightIndex];

  if (photos.length < 2) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Need at least 2 photos to compare
          </p>
        </CardContent>
      </Card>
    );
  }

  const PhotoDisplay = ({
    photo,
    imageUrl,
    loading,
    label,
    onPrev,
    onNext,
    canPrev,
    canNext,
  }: {
    photo: ProgressPhoto;
    imageUrl: string | null;
    loading: boolean;
    label: string;
    onPrev: () => void;
    onNext: () => void;
    canPrev: boolean;
    canNext: boolean;
  }) => (
    <div className="flex-1 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onPrev}
            disabled={!canPrev}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onNext}
            disabled={!canNext}
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="aspect-[3/4] relative bg-muted rounded-lg overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={`${photo.photo_type} progress photo`}
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Failed to load</p>
          </div>
        )}
      </div>

      <div className="text-center">
        <Badge variant="outline" className="mb-1">
          {photoTypeLabels[photo.photo_type] || photo.photo_type}
        </Badge>
        <p className="text-sm font-medium">
          {format(new Date(photo.photo_date), "MMM d, yyyy")}
        </p>
        {photo.caption && (
          <p className="text-xs text-muted-foreground mt-1">{photo.caption}</p>
        )}
      </div>
    </div>
  );

  // Render overlay mode
  if (mode === "overlay") {
    return (
      <div className="space-y-3">
        {/* Mode toggle header */}
        <div className="flex items-center justify-between">
          <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v as CompareMode)}>
            <ToggleGroupItem value="side-by-side" aria-label="Side by side view" className="gap-1.5">
              <Columns2 className="h-4 w-4" />
              <span className="hidden sm:inline">Side by Side</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="overlay" aria-label="Overlay view" className="gap-1.5">
              <Layers className="h-4 w-4" />
              <span className="hidden sm:inline">Overlay</span>
            </ToggleGroupItem>
          </ToggleGroup>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <PhotoOverlayView photos={photos} />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="text-lg">Compare Progress</CardTitle>
            <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v as CompareMode)}>
              <ToggleGroupItem value="side-by-side" aria-label="Side by side view" className="gap-1.5 h-8 px-2">
                <Columns2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">Side by Side</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="overlay" aria-label="Overlay view" className="gap-1.5 h-8 px-2">
                <Layers className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">Overlay</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <PhotoDisplay
            photo={leftPhoto}
            imageUrl={leftUrl}
            loading={loadingLeft}
            label="Before"
            onPrev={() => setLeftIndex((i) => Math.max(0, i - 1))}
            onNext={() =>
              setLeftIndex((i) => Math.min(rightIndex - 1, i + 1))
            }
            canPrev={leftIndex > 0}
            canNext={leftIndex < rightIndex - 1}
          />

          <div className="w-px bg-border" />

          <PhotoDisplay
            photo={rightPhoto}
            imageUrl={rightUrl}
            loading={loadingRight}
            label="After"
            onPrev={() =>
              setRightIndex((i) => Math.max(leftIndex + 1, i - 1))
            }
            onNext={() =>
              setRightIndex((i) => Math.min(photos.length - 1, i + 1))
            }
            canPrev={rightIndex > leftIndex + 1}
            canNext={rightIndex < photos.length - 1}
          />
        </div>

        {/* Timeline indicator */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Oldest</span>
            <div className="flex-1 h-2 bg-muted rounded-full relative">
              <div
                className="absolute h-4 w-4 bg-info rounded-full -top-1 border-2 border-background"
                style={{
                  left: `${(leftIndex / (photos.length - 1)) * 100}%`,
                  transform: "translateX(-50%)",
                }}
              />
              <div
                className="absolute h-4 w-4 bg-success rounded-full -top-1 border-2 border-background"
                style={{
                  left: `${(rightIndex / (photos.length - 1)) * 100}%`,
                  transform: "translateX(-50%)",
                }}
              />
            </div>
            <span className="text-xs text-muted-foreground">Newest</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-info">
              {format(new Date(leftPhoto.photo_date), "MMM d")}
            </span>
            <span className="text-xs text-success">
              {format(new Date(rightPhoto.photo_date), "MMM d")}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoCompareView;
